/**
 * Central multilingual SEO path + URL helpers (FR / EN / AR).
 * Single source of truth for city landings — do not duplicate path logic elsewhere.
 */

import { isCityLandingSlug, SEO_BASE_URL } from '@/lib/seo/city-urls';

export type SEOLocale = 'fr' | 'en' | 'ar';

export const SEO_LOCALES: SEOLocale[] = ['fr', 'en', 'ar'];

/** FR canonical city path (unprefixed). */
export const cityPathFr = (slug: string): string => `/location-voiture/${slug}`;

/** EN city path. */
export const cityPathEn = (slug: string): string => `/en/car-rental/${slug}`;

/** AR city path. */
export const cityPathAr = (slug: string): string => `/ar/location-voiture/${slug}`;

export const cityPath = (slug: string, locale: SEOLocale): string => {
  if (locale === 'en') return cityPathEn(slug);
  if (locale === 'ar') return cityPathAr(slug);
  return cityPathFr(slug);
};

export const cityCanonicalUrl = (slug: string, locale: SEOLocale = 'fr'): string =>
  `${SEO_BASE_URL}${cityPath(slug, locale)}`;

/** Programmatic SEO hub index paths. */
export const seoHubPath = (locale: SEOLocale): string => {
  if (locale === 'en') return '/en/car-rental';
  if (locale === 'ar') return '/ar/location-voiture';
  return '/fr/location-voiture';
};

export const seoHubCanonicalUrl = (locale: SEOLocale): string =>
  `${SEO_BASE_URL}${seoHubPath(locale)}`;

export type ParsedCityLanding =
  | { kind: 'city'; locale: SEOLocale; citySlug: string }
  | null;

/**
 * Detect locale + city slug from pathname (no query).
 */
export const parseCityLandingPath = (pathname: string): ParsedCityLanding => {
  const path = pathname.replace(/\/$/, '') || '/';

  let m = path.match(/^\/location-voiture\/([^/]+)$/);
  if (m && isCityLandingSlug(m[1])) {
    return { kind: 'city', locale: 'fr', citySlug: m[1].toLowerCase() };
  }

  m = path.match(/^\/en\/car-rental\/([^/]+)$/);
  if (m && isCityLandingSlug(m[1])) {
    return { kind: 'city', locale: 'en', citySlug: m[1].toLowerCase() };
  }

  m = path.match(/^\/ar\/location-voiture\/([^/]+)$/);
  if (m && isCityLandingSlug(m[1])) {
    return { kind: 'city', locale: 'ar', citySlug: m[1].toLowerCase() };
  }

  m = path.match(/^\/fr\/location-voiture\/([^/]+)$/);
  if (m && isCityLandingSlug(m[1])) {
    return { kind: 'city', locale: 'fr', citySlug: m[1].toLowerCase() };
  }

  return null;
};

export const localeFromI18nCode = (code: string | undefined): SEOLocale => {
  const base = code?.split('-')[0] ?? 'fr';
  if (base === 'en') return 'en';
  if (base === 'ar') return 'ar';
  return 'fr';
};

export interface CityHreflangCluster {
  fr: string;
  en: string;
  ar: string;
  arMa: string;
  xDefault: string;
}

/** Absolute hreflang cluster for a city slug. */
export const cityHreflangCluster = (citySlug: string): CityHreflangCluster => {
  const fr = cityCanonicalUrl(citySlug, 'fr');
  const en = cityCanonicalUrl(citySlug, 'en');
  const ar = cityCanonicalUrl(citySlug, 'ar');
  return { fr, en, ar, arMa: ar, xDefault: fr };
};
